<template>
    <div class="flex mt-6 justify-between">
        <h1 class="text-2xl font-bold text-gray-800">Reports</h1>
        <div class="mr-6">
            <div
                :class="[
                    'relative',
                    !hasDateRangeAccess && 'cursor-pointer opacity-50',
                ]"
                @click="!hasDateRangeAccess && checkAccessOrShowModal()"
            >
                <DateRangePicker
                    v-model="selectedDateRange"
                    :disabled="!hasDateRangeAccess"
                    :display-full-block="true"
                    @update:model-value="handleDateRangeChange"
                />
                <div
                    v-if="!hasDateRangeAccess"
                    class="absolute inset-0 bg-transparent cursor-pointer"
                    title="Click for upgrade options - Scale plan required"
                />
            </div>
        </div>
    </div>

    <div>
        <BasicTabs :tabs="tabs" :tab-key="'domain-overview'" />
    </div>

    <!-- Upgrade Required Section -->
    <UpgradeRequired v-if="!hasReportsAccess" />

    <!-- Main Content -->
    <div v-else>
        <div class="grid max-h-[189px] gap-5 md:grid-cols-4">
            <ReportBox>
                <DonutChart :data="donutData" />
            </ReportBox>
            <ReportBox>
                <div
                    class="flex h-[48px] w-[48px] items-center justify-center rounded-md bg-blue-500 p-2"
                >
                    <Mail class="text-white"></Mail>
                </div>
                <div class="mt-8">
                    <p class="text-sm font-medium text-gray-600">
                        Total Emails Reported
                    </p>
                    <p class="mt-1 text-4xl font-medium text-black">
                        {{ formatNumber(statistics.totalEmailsReported) }}
                    </p>
                </div>
            </ReportBox>
            <ReportBox>
                <div
                    class="flex h-[48px] w-[48px] items-center justify-center rounded-md bg-green-500 p-2"
                >
                    <Shield class="text-white"></Shield>
                </div>
                <div class="mt-8">
                    <p class="text-sm font-medium text-gray-600">
                        DMARC Compliant
                    </p>
                    <p class="mt-1 text-4xl font-medium text-black">
                        {{ formatNumber(statistics.dmarcCompliant) }}
                    </p>
                </div>
            </ReportBox>
            <ReportBox>
                <div
                    class="flex h-[48px] w-[48px] items-center justify-center rounded-md bg-red-500 p-2"
                >
                    <ShieldAlertIcon class="text-white"></ShieldAlertIcon>
                </div>
                <div class="mt-8">
                    <p class="text-sm font-medium text-gray-600">
                        DMARC Non-Compliant
                    </p>
                    <p class="mt-1 text-4xl font-medium text-black">
                        {{ formatNumber(statistics.dmarcNonCompliant) }}
                    </p>
                </div>
            </ReportBox>
        </div>

        <div class="mt-5 grid grid-cols-2 gap-5">
            <ReportBox>
                <p class="font-medium text-gray-900">Quick Insights</p>

                <div class="mt-4 grid gap-3">
                    <InsightBox
                        v-for="insight in insights"
                        :key="insight.title"
                        :variant="insight.variant"
                    >
                        <template #title>{{ insight.title }}</template>
                        <template #content>
                            <span v-html="insight.content"></span>
                        </template>
                    </InsightBox>
                </div>
            </ReportBox>

            <ReportBox>
                <p class="font-medium text-gray-900">AI Recommendations</p>

                <div v-if="aiAssessment.status === 'processing'" class="mt-4">
                    <div class="flex items-center gap-3">
                        <div
                            class="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-r-transparent"
                        ></div>
                        <span class="text-sm text-gray-600"
                            >Generating AI insights...</span
                        >
                    </div>
                </div>

                <div v-else-if="aiAssessment.recommendations" class="mt-4">
                    <div
                        class="rounded-lg border border-yellow-100 bg-yellow-50 p-4"
                    >
                        <div class="flex items-start gap-4">
                            <div
                                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-yellow-100"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-brain h-5 w-5 text-yellow-600"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
                                    ></path>
                                    <path
                                        d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
                                    ></path>
                                    <path
                                        d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
                                    ></path>
                                    <path
                                        d="M17.599 6.5a3 3 0 0 0 .399-1.375"
                                    ></path>
                                    <path
                                        d="M6.003 5.125A3 3 0 0 0 6.401 6.5"
                                    ></path>
                                    <path
                                        d="M3.477 10.896a4 4 0 0 1 .585-.396"
                                    ></path>
                                    <path
                                        d="M19.938 10.5a4 4 0 0 1 .585.396"
                                    ></path>
                                    <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                                    <path
                                        d="M19.967 17.484A4 4 0 0 1 18 18"
                                    ></path>
                                </svg>
                            </div>
                            <div class="flex-1">
                                <h5
                                    class="mb-2 mt-1 font-medium text-yellow-900"
                                >
                                    AI Recommendations
                                </h5>
                                <div
                                    class="text-md mb-3 text-sm leading-8 text-yellow-800"
                                    v-html="aiAssessment.recommendations"
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="mt-4">
                    <div class="flex items-center gap-3 text-gray-500">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-brain h-5 w-5"
                        >
                            <path
                                d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
                            ></path>
                            <path
                                d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
                            ></path>
                            <path
                                d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
                            ></path>
                            <path d="M17.599 6.5a3 3 0 0 0 .399-1.375"></path>
                            <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"></path>
                            <path d="M3.477 10.896a4 4 0 0 1 .585-.396"></path>
                            <path d="M19.938 10.5a4 4 0 0 1 .585.396"></path>
                            <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                            <path d="M19.967 17.484A4 4 0 0 1 18 18"></path>
                        </svg>
                        <span class="text-sm"
                            >AI recommendations will appear here when
                            available</span
                        >
                    </div>
                </div>
            </ReportBox>
        </div>

        <div class="mt-5">
            <StatsChart
                v-if="statistics.totalEmailsReported > 0"
                :chart-data="chartData"
                :header="$t('vue.daily_email_reports')"
            ></StatsChart>
        </div>

        <div v-if="activeDomains.length > 0" class="mt-5 mb-24">
            <DomainsTable :active-domains="activeDomains" />
        </div>
    </div>

    <UpgradeRequiredModal
        :opened="showUpgradeModal"
        title="Scale Plan Required"
        description="Custom date range selection requires a Scale plan subscription. Upgrade your subscription to access advanced filtering features."
        @close="closeUpgradeModal"
    />
</template>

<script lang="ts" setup>
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import UpgradeRequired from "@/components/Atomic/Miscellaneous/UpgradeRequired.vue";
import UpgradeRequiredModal from "@/components/Atomic/Modals/UpgradeRequiredModal.vue";
import useReportsAccess from "@/composables/useReportsAccess";
import useDateRangeAccess from "@/composables/useDateRangeAccess";
import useNumberFormat from "@/composables/useNumberFormat";
import InsightBox from "./components/InsightBox.vue";
import DateRangePicker from "./components/DateRangePicker.vue";
import ReportBox from "./components/ReportBox.vue";
import DonutChart from "./components/DonutChart/DonutChart.vue";
import StatsChart from "./components/StatsChart.vue";
import DomainsTable from "./components/DomainsTable.vue";
import {
    Globe,
    AlertOctagon,
    Settings,
    Mail,
    Shield,
    ShieldAlertIcon,
} from "lucide-vue-next";
import { ref, computed, onMounted } from "vue";
import { getLocalTimeZone, today, parseDate } from "@internationalized/date";
import type { DateRange } from "reka-ui";
import { defineProps } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import axios from "axios";

import type TabItem from "@/types/Settings/TabItem";
import Notifications from "@/components/Notifications.vue";
import DateRangeField from "@/Pages/Reports/components/DateRangeField.vue";

const { hasReportsAccess } = useReportsAccess();
const {
    hasDateRangeAccess,
    showUpgradeModal,
    checkAccessOrShowModal,
    closeUpgradeModal,
} = useDateRangeAccess();
const { formatNumber } = useNumberFormat();

const props = defineProps<{
    statistics: {
        activeDomains: number;
        totalEmailsReported: number;
        dmarcCompliant: number;
        dmarcNonCompliant: number;
        complianceRate: number;
    };
    activeDomains: Array<{
        domain: string;
        active: boolean;
        dmarcPolicy: string;
        report_current: {
            pass: number;
            fail: number;
            sum: number;
        };
    }>;
    chartData: Array<{
        day: string;
        pass: number;
        fail: number;
    }>;
    dateRange: {
        start: string;
        end: string;
    };
    insights: Array<{
        variant: string;
        title: string;
        content: string;
    }>;
}>();

const donutData = computed(() => [
    { key: "compliant", value: props.statistics.complianceRate },
    { key: "non-compliant", value: 100 - props.statistics.complianceRate },
]);

// AI Assessment data
const aiAssessment = ref<any>({});

// Initialize date range from backend data
const selectedDateRange = ref<DateRange>({
    start: parseDate(props.dateRange.start),
    end: parseDate(props.dateRange.end),
});

// Handle date range changes when user selects new dates
const handleDateRangeChange = (newRange: DateRange) => {
    // Check if user has access to date range picker
    if (!checkAccessOrShowModal()) {
        return;
    }

    // Skip if range is incomplete
    if (!newRange.start || !newRange.end) {
        return;
    }

    const startDate = newRange.start.toString();
    const endDate = newRange.end.toString();

    // Make the request to update data
    router.get(
        route("reports.index"),
        {
            start_date: startDate,
            end_date: endDate,
        },
        {
            preserveState: false,
            preserveScroll: true,
        },
    );
};

// Fetch AI Assessment data
const fetchAiAssessment = async () => {
    try {
        // const res = await axios.get(route("reports.ai"));
        // aiAssessment.value = res.data;
        // if (res.data.status === "processing") {
        //     setTimeout(fetchAiAssessment, 5000);
        // }
    } catch (e) {
        console.error(e);
    }
};

// Fetch AI recommendations when component mounts
onMounted(fetchAiAssessment);

const tabs: TabItem[] = [
    {
        title: "Domain Overview",
        key: "domain-overview",
        icon: Globe,
        url: "/reports",
    },
    {
        title: "Settings",
        key: "settings",
        icon: Settings,
        url: "/reports/settings",
    },
];
</script>
